/**
 * Customer browse display categories (Home + Product Catalog).
 * Mapping does not write Product Management Category by itself.
 * Product Manager "Unify to Home categories" is a separate operator write that copies
 * these same strings onto `products_{key}.Category`.
 *
 * Display headers:
 *   SMOKED SALMON | WHOLE MUSCLE CHARCUTERIE | SALAMI CHARCUTERIE | AGED BACON | FRESH SAUSAGE | BOXES & HAMPERS
 */

import { formatCategoryChipLabel, isSalmonBrowseCategory, sanitizeCategoryText } from './browseCategoryUtils';

export const DISPLAY_SMOKED_SALMON = 'SMOKED SALMON';
export const DISPLAY_WHOLE_MUSCLE_CHARCUTERIE = 'WHOLE MUSCLE CHARCUTERIE';
export const DISPLAY_SALAMI_CHARCUTERIE = 'SALAMI CHARCUTERIE';
export const DISPLAY_AGED_BACON = 'AGED BACON';
export const DISPLAY_FRESH_SAUSAGE = 'FRESH SAUSAGE';
export const DISPLAY_BOXES_HAMPERS = 'BOXES & HAMPERS';

/** Fixed section order on Home + Product Catalog. */
export const CUSTOMER_DISPLAY_CATEGORY_ORDER = Object.freeze([
    DISPLAY_SMOKED_SALMON,
    DISPLAY_WHOLE_MUSCLE_CHARCUTERIE,
    DISPLAY_SALAMI_CHARCUTERIE,
    DISPLAY_AGED_BACON,
    DISPLAY_FRESH_SAUSAGE,
    DISPLAY_BOXES_HAMPERS,
]);

const WHOLE_MUSCLE_CODE_RE = /^CH-(BRES|COPP|LONZ|PANC)/i;
const SALAMI_CODE_RE =
    /^CH-(NDSM|HOSA|FESA|CHSA|PPSA|SNAK|SNHS|PBSA|CHOR|SAUC)/i;
const SMOKED_SALMON_CODE_RE = /^SA-/i;
const AGED_BACON_CODE_RE = /^BA-/i;
const FRESH_SAUSAGE_CODE_RE = /^FS-/i;
const BOX_CODE_RE = /^BX-/i;

function lc(value) {
    return String(value ?? '').toLowerCase();
}

function productName(product) {
    return lc(product?.name ?? product?.Name);
}

function productCode(product) {
    return String(product?.code ?? product?.Code ?? '').trim();
}

function productPmCategory(product) {
    return sanitizeCategoryText(product?.category ?? product?.Category ?? product?.categoryLabel);
}

function isWholeMuscleCharcuterie(product) {
    const code = productCode(product);
    const name = productName(product);

    if (WHOLE_MUSCLE_CODE_RE.test(code)) return true;

    if (/\bbresaola\b/.test(name)) return true;
    if (/\bcoppa\b/.test(name)) return true;
    if (/\blonza\b/.test(name)) return true;
    if (/\bpancetta\b/.test(name)) return true;
    if (name.includes('whole muscle') && !name.includes('salami')) return true;

    return false;
}

function isSalamiCharcuterie(product) {
    const code = productCode(product);
    const name = productName(product);

    if (SALAMI_CODE_RE.test(code)) return true;

    if (name.includes('salami')) return true;
    if (name.includes("'nduja") || name.includes('nduja')) return true;
    if (name.includes('pepperoni')) return true;
    if (name.includes('saucisson')) return true;
    if (name.includes('whole salami')) return true;
    if (/\bchorizo\b/.test(name) && !name.includes('sausage')) return true;

    return false;
}

function isAgedBacon(product) {
    const code = productCode(product);
    const name = productName(product);
    const pmCat = lc(productPmCategory(product));

    if (AGED_BACON_CODE_RE.test(code)) return true;
    if (pmCat.includes('bacon')) return true;
    if (name.includes('bacon') && !name.includes('sausage')) return true;
    if (name.includes('lardon')) return true;

    return false;
}

function isFreshSausage(product) {
    const code = productCode(product);
    const name = productName(product);
    const pmCat = lc(productPmCategory(product));

    if (FRESH_SAUSAGE_CODE_RE.test(code)) return true;
    if (pmCat.includes('sausage')) return true;
    if (name.includes('sausage')) return true;

    return false;
}

function isSmokedSalmon(product) {
    const code = productCode(product);
    const pmCat = productPmCategory(product);

    if (SMOKED_SALMON_CODE_RE.test(code)) return true;
    if (isSalmonBrowseCategory(pmCat)) return true;
    if (isSalmonBrowseCategory(productName(product))) return true;

    return false;
}

function isBoxesHampers(product) {
    const code = productCode(product);
    const pmCat = lc(productPmCategory(product));

    if (product?.isBox === true || product?.IsBox === true) return true;
    if (BOX_CODE_RE.test(code) || /-BOX$/i.test(code)) return true;
    if (pmCat.includes('box') || pmCat.includes('hamper')) return true;

    return false;
}

/**
 * Resolve browse-only display category for a product row.
 * @param {object} product — { name, Name, code, Code, category, Category, categoryLabel, packSize, Pack Size }
 * @returns {string} one of CUSTOMER_DISPLAY_CATEGORY_ORDER (or best-effort label)
 */
export function resolveCustomerDisplayCategory(product) {
    if (!product || typeof product !== 'object') return 'Uncategorized';

    if (isBoxesHampers(product)) return DISPLAY_BOXES_HAMPERS;
    if (isSmokedSalmon(product)) return DISPLAY_SMOKED_SALMON;
    if (isFreshSausage(product)) return DISPLAY_FRESH_SAUSAGE;
    if (isAgedBacon(product)) return DISPLAY_AGED_BACON;

    const pmCat = lc(productPmCategory(product));
    const isCharcuterie =
        pmCat.includes('charcuterie') ||
        productName(product).includes('charcuterie') ||
        /^CH-/i.test(productCode(product));

    if (isCharcuterie) {
        if (isWholeMuscleCharcuterie(product)) return DISPLAY_WHOLE_MUSCLE_CHARCUTERIE;
        if (isSalamiCharcuterie(product)) return DISPLAY_SALAMI_CHARCUTERIE;
        return DISPLAY_SALAMI_CHARCUTERIE;
    }

    if (isWholeMuscleCharcuterie(product)) return DISPLAY_WHOLE_MUSCLE_CHARCUTERIE;
    if (isSalamiCharcuterie(product)) return DISPLAY_SALAMI_CHARCUTERIE;

    const fallback = sanitizeCategoryText(productPmCategory(product));
    return fallback || 'Uncategorized';
}

/**
 * Order present display categories for browse UI (ignores PM categories_meta).
 * @param {string[]} present — category keys present in loaded products
 */
export function mergeCustomerDisplayCategoryOrder(present = []) {
    const set = new Set(
        (present || []).map((item) => sanitizeCategoryText(item)).filter(Boolean)
    );
    const ordered = CUSTOMER_DISPLAY_CATEGORY_ORDER.filter((key) => set.has(key));
    for (const key of set) {
        if (!ordered.includes(key)) ordered.push(key);
    }
    return ordered;
}

/**
 * Exact brochure section heading / chip label (ALL CAPS, full name).
 * SMOKED SALMON, WHOLE MUSCLE CHARCUTERIE, SALAMI CHARCUTERIE, AGED BACON, FRESH SAUSAGE, BOXES & HAMPERS
 */
export function formatBrowseDisplayCategoryLabel(categoryKey) {
    const key = sanitizeCategoryText(categoryKey);
    if (!key || key === 'All') return key;

    const upper = key.toUpperCase();
    const match = CUSTOMER_DISPLAY_CATEGORY_ORDER.find((c) => c === key || c === upper);
    if (match) return match;

    return key;
}

/**
 * Home browse: exact brochure names for display categories; shorter labels for specials.
 */
export function formatBrowseCategoryChipLabel(categoryKey) {
    const exact = formatBrowseDisplayCategoryLabel(categoryKey);
    if (CUSTOMER_DISPLAY_CATEGORY_ORDER.includes(exact)) return exact;
    return formatCategoryChipLabel(categoryKey);
}
